import { completeSearchResults, resultPageURL, searchBatchURL, searchPage, selectedSort, sortSearchResults, visiblePageNumbers } from './du-search-model.js';

class DurasanSearchResults extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.status = this.querySelector('[data-du-search-status]');
    this.sortSelect = this.querySelector('[data-du-search-sort]');
    this.retry = this.querySelector('[data-du-search-retry]');
    this.grid = this.querySelector('[data-du-search-grid]');
    this.pagination = this.querySelector('[data-du-search-pagination]');
    this.form = this.querySelector('[data-du-search-form]');
    this.addEventListener('change', this.onChange, { signal: this.controller.signal });
    this.addEventListener('click', this.onClick, { signal: this.controller.signal });
    this.form.addEventListener('formdata', this.onFormData, { signal: this.controller.signal });
    window.addEventListener('popstate', this.onPopState, { signal: this.controller.signal });
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'preview_theme_id';
      input.value = preview;
      this.form.append(input);
    }
    this.load();
  }

  disconnectedCallback() {
    this.controller.abort();
    this.requestController?.abort();
  }

  async load() {
    this.requestController?.abort();
    this.requestController = new AbortController();
    this.setAttribute('aria-busy', 'true');
    this.status.textContent = this.dataset.loading;
    this.retry.hidden = true;
    this.sortSelect.disabled = true;
    const url = new URL(window.location.href);
    this.queryKey = searchBatchURL(url, 1).href;
    try {
      const signal = AbortSignal.any([this.controller.signal, this.requestController.signal, AbortSignal.timeout(30000)]);
      const products = await completeSearchResults(async page => {
        const response = await fetch(searchBatchURL(url, page), { signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Search request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = document.querySelector('[data-du-search-batch]');
        if (!data) throw new Error('Search batch missing');
        return JSON.parse(data.textContent);
      });
      if (!this.isConnected || signal.aborted) return;
      this.products = products;
      this.sortSelect.disabled = false;
      this.status.textContent = '';
      this.querySelector('[data-du-search-fallback]').hidden = true;
      const oldResult = this.querySelector('#ResultsList');
      if (oldResult) oldResult.id = 'DuSearchNativeResults';
      const enhanced = this.querySelector('[data-du-search-enhanced]');
      enhanced.id = 'ResultsList';
      enhanced.hidden = false;
      const count = document.querySelector('.du-search-page__count');
      if (count) count.textContent = this.dataset.countLabel.replace('__COUNT__', String(products.length));
      this.render(false);
    } catch (error) {
      if (this.controller.signal.aborted || this.requestController.signal.aborted) return;
      this.status.textContent = error.message === 'Search limit' ? this.dataset.tooMany : this.dataset.error;
      this.retry.hidden = error.message === 'Search limit';
    } finally {
      this.setAttribute('aria-busy', 'false');
    }
  }

  render(focus) {
    const url = new URL(window.location.href);
    const sort = selectedSort(url);
    this.sortSelect.value = sort;
    const result = searchPage(sortSearchResults(this.products, sort, document.documentElement.lang || 'pl'), url.searchParams.get('page'));
    const canonical = resultPageURL(url, result.page, sort);
    if (canonical.href !== url.href) history.replaceState(null, '', canonical);
    this.grid.replaceChildren(...result.items.map(product => {
      const item = document.createElement('li');
      item.dataset.productId = product.id;
      item.dataset.productTitle = product.title;
      item.innerHTML = product.html;
      return item;
    }));
    this.querySelector('[data-du-search-empty]').hidden = this.products.length > 0;
    this.pagination.replaceChildren();
    this.pagination.hidden = result.pages <= 1;
    if (result.pages > 1) {
      const list = document.createElement('ul');
      list.className = 'pagination__list';
      if (result.page > 1) list.append(this.pageLink(canonical, result.page - 1, this.dataset.previousLabel, false));
      let previous = 0;
      for (const page of visiblePageNumbers(result.page, result.pages)) {
        if (page - previous > 1) {
          const gap = document.createElement('li');
          gap.className = 'pagination__item pagination__item--gap';
          gap.textContent = '…';
          gap.setAttribute('aria-hidden', 'true');
          list.append(gap);
        }
        list.append(this.pageLink(canonical, page, String(page), page === result.page));
        previous = page;
      }
      if (result.page < result.pages) list.append(this.pageLink(canonical, result.page + 1, this.dataset.nextLabel, false));
      this.pagination.append(list);
    }
    if (focus) {
      this.grid.tabIndex = -1;
      this.grid.focus({ preventScroll: true });
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }

  pageLink(url, page, label, active) {
    const item = document.createElement('li');
    item.className = 'pagination__item';
    const link = document.createElement('a');
    const numbered = /^\d+$/.test(label);
    link.className = `pagination__link ${numbered ? 'pagination__link--page' : 'pagination__link--arrow'}${active ? ' pagination__link--current' : ''}`;
    link.href = resultPageURL(url, page, selectedSort(url)).href;
    link.dataset.duSearchPage = String(page);
    link.textContent = numbered ? label : label === this.dataset.previousLabel ? '←' : '→';
    link.setAttribute('aria-label', numbered ? this.dataset.pageLabel.replace('__PAGE__', label) : label);
    if (active) link.setAttribute('aria-current', 'page');
    item.append(link);
    return item;
  }

  onClick = event => {
    if (event.target.closest('[data-du-search-retry]')) return void this.load();
    const link = event.target.closest('[data-du-search-page]');
    if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    history.pushState(null, '', link.href);
    this.render(true);
  };

  onChange = event => {
    if (event.target !== this.sortSelect || !this.products) return;
    history.pushState(null, '', resultPageURL(window.location.href, 1, this.sortSelect.value));
    this.render(false);
  };

  onFormData = event => {
    for (const input of this.form.querySelectorAll('input[type="number"]')) {
      if (input.value === '') event.formData.delete(input.name);
    }
  };

  onPopState = () => {
    if (this.queryKey !== searchBatchURL(new URL(window.location.href), 1).href) return void window.location.reload();
    if (this.products) this.render(true);
  };
}

if (!customElements.get('du-search-results')) customElements.define('du-search-results', DurasanSearchResults);
